import { listOrgEnrollments } from "./enrollmentHelpers"
import { listCourses } from "./courseHelpers"

const SCORE_BUCKETS = [
  { key: "90-100", label: "90–100", min: 90, max: 100 },
  { key: "80-89", label: "80–89", min: 80, max: 89 },
  { key: "70-79", label: "70–79", min: 70, max: 79 },
  { key: "60-69", label: "60–69", min: 60, max: 69 },
  { key: "below60", label: "<60", min: 0, max: 59 },
]

const bucketFor = (score) => SCORE_BUCKETS?.find((b) => score >= b?.min && score <= b?.max)?.key || "below60"

// Flattens every attempted module-quiz and capstone across all enrollments
// into one list of { assessmentKey, title, score, passed } records — the
// unit both Score Distribution and Most Challenging Assessments are built from.
const flattenAttempts = (enrollments, courseTitleMap, quizTitleMap) => {
  const attempts = []

  enrollments?.forEach((enrollment) => {
    Object.entries(enrollment?.moduleQuizResults || {})?.forEach(([moduleId, result]) => {
      if (!result?.attemptCount) return
      const key = `${enrollment?.courseId}::${moduleId}`
      attempts.push({
        assessmentKey: key,
        title: quizTitleMap?.get(key) || "Module Quiz",
        score: result?.bestScore || 0,
        passed: !!result?.passed,
      })
    })

    if (enrollment?.capstoneResult?.attemptCount) {
      const key = `${enrollment?.courseId}::capstone`
      attempts.push({
        assessmentKey: key,
        title: quizTitleMap?.get(key) || `${courseTitleMap?.get(enrollment?.courseId) || "Course"} Capstone`,
        score: enrollment?.capstoneResult?.bestScore || 0,
        passed: !!enrollment?.capstoneResult?.passed,
      })
    }
  })

  return attempts
}

export const computeAssessmentPerformance = async (orgId) => {
  const [{ enrollments }, { courses }] = await Promise.all([listOrgEnrollments(orgId), listCourses(orgId)])

  const courseTitleMap = new Map(courses?.map((c) => [c?.courseId, c?.title]))
  const quizTitleMap = new Map()
  courses?.forEach((course) => {
    course?.modules?.forEach((module) => {
      if (module?.quiz) quizTitleMap?.set(`${course?.courseId}::${module?.id}`, module?.quiz?.title || module?.title)
    })
    if (course?.capstone) quizTitleMap?.set(`${course?.courseId}::capstone`, course?.capstone?.title || `${course?.title} Capstone`)
  })

  const attempts = flattenAttempts(enrollments, courseTitleMap, quizTitleMap)

  const totalCompleted = attempts?.length
  const avgScore = totalCompleted ? Math.round(attempts?.reduce((sum, a) => sum + a?.score, 0) / totalCompleted) : 0
  const passCount = attempts?.filter((a) => a?.passed)?.length || 0
  const passRate = totalCompleted ? Math.round((passCount / totalCompleted) * 1000) / 10 : 0

  const distribution = SCORE_BUCKETS?.map((bucket) => ({
    key: bucket?.key,
    label: bucket?.label,
    count: attempts?.filter((a) => bucketFor(a?.score) === bucket?.key)?.length,
  }))

  // Group by assessment identity to find the ones learners struggle with most.
  const byAssessment = new Map()
  attempts?.forEach((a) => {
    const existing = byAssessment?.get(a?.assessmentKey) || { title: a?.title, total: 0, passed: 0 }
    existing.total += 1
    if (a?.passed) existing.passed += 1
    byAssessment?.set(a?.assessmentKey, existing)
  })

  const mostChallenging = Array.from(byAssessment?.values())
    ?.map((a) => ({ title: a?.title, passRate: a?.total ? Math.round((a?.passed / a?.total) * 1000) / 10 : 0, attempts: a?.total }))
    ?.sort((a, b) => a?.passRate - b?.passRate)
    ?.slice(0, 5)

  return { totalCompleted, avgScore, passRate, distribution, mostChallenging }
}